// Tracers and block-particle effects (hits, explosions, smoke) — all
// instanced cubes, in keeping with the world.

import * as THREE from "three";

// Tracer glow, like the original prototype: warm yellow-white for everyone
// (no team tint — you read ownership from where the fire comes from).
const TRACER_CORE = new THREE.Color(0xfff7b0);
const TRACER_FLAK = new THREE.Color(0xffd98a); // heavy AA burns a touch warmer

/**
 * Bullet rendering in the original prototype's style: most rounds are
 * invisible and only drag a faint white vapor trail; every Nth round (per
 * the caliber table) is a bright tracer whose length/width come from the
 * caliber. AA guns always fire tracers.
 */
export class Tracers {
  constructor(scene, calibers = [], capacity = 512) {
    this.capacity = capacity;
    this.calibers = calibers;

    // Bright tracer rounds (unit cube, scaled per caliber).
    this.mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false }),
      capacity,
    );
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(capacity * 3),
      3,
    );
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);

    // Vapor trails: translucent white streaks behind EVERY round.
    this.trailMesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.2,
        depthWrite: false,
        fog: false,
      }),
      capacity,
    );
    this.trailMesh.frustumCulled = false;
    scene.add(this.trailMesh);

    this.dummy = new THREE.Object3D();
    this.bullets = []; // {pos, vel, team, kind, cal, tracer}
  }

  /** Replace the bullet set from a snapshot (rows: [x,y,z,vx,vy,vz,team,kind,caliber,tracer]). */
  setFromSnapshot(rows) {
    this.bullets.length = 0;
    for (const r of rows) {
      this.bullets.push({
        pos: new THREE.Vector3(r[0], r[1], r[2]),
        vel: new THREE.Vector3(r[3], r[4], r[5]),
        team: r[6] | 0,
        kind: r[7] | 0,
        cal: this.calibers[r[8] | 0] ?? this.calibers[0],
        tracer: !!r[9],
      });
    }
  }

  update(dt) {
    const n = Math.min(this.bullets.length, this.capacity);
    let tracerN = 0;
    for (let i = 0; i < n; i++) {
      const b = this.bullets[i];
      b.pos.addScaledVector(b.vel, dt); // advance between snapshots
      const speed = b.vel.length();
      const cal = b.cal ?? {};

      // Vapor trail: a thin streak from the round's tail backward.
      const trailLen = Math.max(6, speed * (cal.trailSec ?? 0.12));
      const tw = cal.trailWidthM ?? 0.2;
      this.dummy.position.copy(b.pos);
      if (speed > 1) {
        this.dummy.position.addScaledVector(b.vel, -trailLen / (2 * speed));
      }
      this.dummy.lookAt(b.pos.x + b.vel.x, b.pos.y + b.vel.y, b.pos.z + b.vel.z);
      this.dummy.scale.set(tw, tw, trailLen);
      this.dummy.updateMatrix();
      this.trailMesh.setMatrixAt(i, this.dummy.matrix);

      // Tracer rounds: bright caliber-sized streak; the rest stay invisible
      // (only their vapor trail shows), like the original.
      if (!b.tracer) continue;
      this.dummy.position.copy(b.pos);
      this.dummy.lookAt(b.pos.x + b.vel.x, b.pos.y + b.vel.y, b.pos.z + b.vel.z);
      this.dummy.scale.set(cal.tracerWidthM ?? 0.4, cal.tracerWidthM ?? 0.4, cal.tracerLengthM ?? 6);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(tracerN, this.dummy.matrix);
      this.mesh.setColorAt(tracerN, b.kind === 1 ? TRACER_FLAK : TRACER_CORE);
      tracerN++;
    }
    this.trailMesh.count = n;
    this.trailMesh.instanceMatrix.needsUpdate = true;
    this.mesh.count = tracerN;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}

/**
 * Falling gravity bombs (and battleship main-battery shells, which arrive in
 * the same snapshot section): chunky dark cylinders-of-boxes oriented along
 * their velocity, advanced under gravity between snapshots. Shells render
 * bigger and drag a faint tracer glow so a salvo arcing overhead reads.
 */
export class Bombs {
  constructor(scene, capacity = 128, starLightRadius = 1700) {
    this.capacity = capacity;
    this.starLightRadius = starLightRadius;
    this.mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.7, 0.7, 2.2),
      new THREE.MeshLambertMaterial({ color: 0x23262c }),
      capacity,
    );
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);

    // Shell glow: a warm streak trailing each main-battery round.
    this.glowMesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: 0xffc88a,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        fog: false,
      }),
      32,
    );
    this.glowMesh.frustumCulled = false;
    scene.add(this.glowMesh);

    // Torpedo wake: a foamy white streak on the surface behind each fish.
    this.wakeMesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: 0xeaf6fc,
        transparent: true,
        opacity: 0.65,
        depthWrite: false,
        fog: false,
      }),
      32,
    );
    this.wakeMesh.frustumCulled = false;
    scene.add(this.wakeMesh);

    // Star-shell flare: a brilliant burning core with a big additive halo, and
    // a pooled point light that actually lifts the night off the ships below.
    this.flareMesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0xfff0b0, transparent: true, opacity: 0.95, depthWrite: false, fog: false }),
      16,
    );
    this.flareMesh.frustumCulled = false;
    scene.add(this.flareMesh);
    this.flareGlow = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: 0xffe39a, transparent: true, opacity: 0.45,
        blending: THREE.AdditiveBlending, depthWrite: false, fog: false,
      }),
      16,
    );
    this.flareGlow.frustumCulled = false;
    scene.add(this.flareGlow);
    // A few real lights follow the nearest live flares so hulls under them glow.
    this.flareLights = [];
    for (let i = 0; i < 4; i++) {
      // decay 0: an even pool of light out to the radius (a flare hangs ~400 m
      // up, so physical inverse-square falloff would never reach the sea).
      const L = new THREE.PointLight(0xffe6a8, 0, this.starLightRadius, 0);
      L.castShadow = false;
      scene.add(L);
      this.flareLights.push(L);
    }

    // Rocket motor: a hot orange exhaust streak behind each round (its own
    // mesh so the color reads apart from the golden shell glow).
    this.motorMesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: 0xff9a3d,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        fog: false,
      }),
      48,
    );
    this.motorMesh.frustumCulled = false;
    scene.add(this.motorMesh);

    this.dummy = new THREE.Object3D();
    this.bombs = []; // {pos, vel, shell, torp, flare, msl, rocket}
  }

  /** Replace the bomb set from a snapshot
   * (rows: [x,y,z,vx,vy,vz,team,shell,torpedo,flare,missile,rocket]). */
  setFromSnapshot(rows) {
    this.bombs.length = 0;
    for (const r of rows) {
      if (r[12]) continue; // chute bit: parachutes ride this section but Chutes draws them
      this.bombs.push({
        pos: new THREE.Vector3(r[0], r[1], r[2]),
        vel: new THREE.Vector3(r[3], r[4], r[5]),
        team: r[6],
        shell: !!r[7],
        torp: !!r[8],
        flare: !!r[9],
        msl: !!r[10], // land wire-guided missile: flies level, drags a motor glow
        rocket: !!r[11],
      });
    }
  }

  update(dt) {
    const n = Math.min(this.bombs.length, this.capacity);
    let meshN = 0;
    let glowN = 0;
    let wakeN = 0;
    let flareN = 0;
    let motorN = 0;
    this.time = (this.time || 0) + dt;
    for (let i = 0; i < n; i++) {
      const b = this.bombs[i];
      // Star-shell flares drift down at the server's slow rate (no gravity) and
      // render as a burning core + a big additive halo, not a tumbling bomb.
      if (b.flare) {
        b.pos.addScaledVector(b.vel, dt);
        if (flareN < 16) {
          const flick = 0.85 + 0.15 * Math.sin(this.time * 16 + i);
          this.dummy.position.copy(b.pos);
          this.dummy.scale.setScalar(11 * flick);
          this.dummy.updateMatrix();
          this.flareMesh.setMatrixAt(flareN, this.dummy.matrix);
          this.dummy.scale.setScalar(60 * flick);
          this.dummy.updateMatrix();
          this.flareGlow.setMatrixAt(flareN, this.dummy.matrix);
          flareN++;
        }
        continue;
      }
      // Running torpedoes hold their depth, wire-guided missiles fly their
      // guided line (the server steers them; between snapshots hold course),
      // rockets ride a motor-flattened arc (mirror the server's
      // rocketGravityFrac); everything else falls.
      const running = b.torp && Math.abs(b.vel.y) < 0.5;
      if (!running && !b.msl) b.vel.y -= 9.81 * (b.rocket ? 0.3 : 1) * dt;
      b.pos.addScaledVector(b.vel, dt);
      this.dummy.position.copy(b.pos);
      this.dummy.lookAt(b.pos.x + b.vel.x, b.pos.y + b.vel.y, b.pos.z + b.vel.z);
      // Shells stay modest — a salvo should read as distinct rounds, not one
      // merged blob (with multi-turret salvos there are 2-6 in close company).
      const s = b.shell ? 1.2 : b.torp ? 2.6 : b.msl ? 1.7 : b.rocket ? 0.8 : 1;
      this.dummy.scale.set(s, s, s);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(meshN++, this.dummy.matrix);

      if ((b.shell || b.msl) && glowN < 32) {
        const speed = b.vel.length();
        const len = Math.max(6, speed * (b.msl ? 0.1 : 0.03));
        this.dummy.position.copy(b.pos);
        if (speed > 1) this.dummy.position.addScaledVector(b.vel, -len / (2 * speed));
        this.dummy.scale.set(0.55, 0.55, len);
        this.dummy.updateMatrix();
        this.glowMesh.setMatrixAt(glowN++, this.dummy.matrix);
      }
      if (b.rocket && motorN < 48) {
        // Motor plume: a short hot streak off the tail of the round.
        const speed = b.vel.length();
        const len = Math.max(6, speed * 0.045);
        this.dummy.position.copy(b.pos);
        if (speed > 1) this.dummy.position.addScaledVector(b.vel, -(len / 2 + 1.2) / speed);
        this.dummy.scale.set(0.7, 0.7, len);
        this.dummy.updateMatrix();
        this.motorMesh.setMatrixAt(motorN++, this.dummy.matrix);
      }
      if (running && wakeN < 32) {
        // Wake rides the surface just above the fish, trailing aft.
        const speed = b.vel.length();
        const len = Math.max(14, speed * 1.3);
        this.dummy.position.set(b.pos.x, b.pos.y + 0.8, b.pos.z);
        if (speed > 1) this.dummy.position.addScaledVector(b.vel, -len / (2 * speed));
        this.dummy.lookAt(b.pos.x + b.vel.x, b.pos.y + 0.8 + b.vel.y, b.pos.z + b.vel.z);
        this.dummy.scale.set(1.6, 0.3, len);
        this.dummy.updateMatrix();
        this.wakeMesh.setMatrixAt(wakeN++, this.dummy.matrix);
      }
    }
    this.mesh.count = meshN;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.glowMesh.count = glowN;
    this.glowMesh.instanceMatrix.needsUpdate = true;
    this.wakeMesh.count = wakeN;
    this.wakeMesh.instanceMatrix.needsUpdate = true;
    this.flareMesh.count = flareN;
    this.flareMesh.instanceMatrix.needsUpdate = true;
    this.flareGlow.count = flareN;
    this.flareGlow.instanceMatrix.needsUpdate = true;
    this.motorMesh.count = motorN;
    this.motorMesh.instanceMatrix.needsUpdate = true;

    // Point the light pool at the first few live flares so the hulls beneath
    // them lift out of the dark; park the rest dark.
    const flares = this.bombs.filter((b) => b.flare);
    for (let i = 0; i < this.flareLights.length; i++) {
      const L = this.flareLights[i];
      if (i < flares.length) {
        L.position.copy(flares[i].pos);
        L.intensity = 2.2;
      } else {
        L.intensity = 0;
      }
    }
  }
}

// Naval smoke screens: a low grey bank of voxel puffs over the water that hides
// the hulls inside it. Fed [x, z, team, lifeFrac] rows from the snapshot.
export class Smoke {
  /** space = the Brick Moon's chaff clusters: the same instanced puffs, but
   *  placed as a glinting BALL at the row's own altitude (rows carry y out
   *  there) instead of a low bank on the water — foil coasting an orbit. */
  constructor(scene, radius = 700, space = false) {
    this.space = space;
    this.radius = radius;
    // Many small, low-opacity puffs that overlap into a soft bank: the centre
    // stacks deep enough to read solid while the ragged edges stay wispy —
    // smoke, not a stack of crates. (A handful of big opaque boxes reads as
    // blocks under the directional light.)
    this.puffN = 96;
    this.cap = this.puffN * 8;
    this.mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({ color: 0xb9c2cb, transparent: true, opacity: 0.34, depthWrite: false }),
      this.cap,
    );
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.dummy = new THREE.Object3D();
    this.rows = [];
    this.waterY = 0;
    // Deterministic golden-angle puff layout across the disc so a cloud sits
    // still rather than shimmering frame to frame. Each puff carries its own
    // size, a lift fraction, and a yaw so the bank's top is lumpy, not a slab.
    this.puffs = [];
    for (let i = 0; i < this.puffN; i++) {
      const a = i * 2.39996;
      const rr = Math.sqrt(i / this.puffN);
      const sc = 0.6 + (i % 5) * 0.1;
      const lift = 0.4 + 0.6 * Math.abs(Math.sin(i * 1.7)) * (1 - rr); // taller in the middle
      this.puffs.push([Math.cos(a) * rr, Math.sin(a) * rr, sc, lift, a]);
    }
  }
  setFromSnapshot(rows, waterY) {
    this.rows = rows || [];
    this.waterY = waterY ?? this.waterY;
  }
  update() {
    let n = 0;
    for (const r of this.rows) {
      const x = r[0], z = r[1]; // [x, z, team, life, y(space)]
      for (const [ox, oz, sc, lift, yaw] of this.puffs) {
        if (n >= this.cap) break;
        if (this.space) {
          // A cluster, not a bank: cubes scattered through a ball around the
          // wire's own altitude, sized to the decoy radius. lift doubles as
          // the vertical scatter; life fades the foil as it disperses.
          const s = this.radius * 0.16 * sc;
          this.dummy.position.set(
            x + ox * this.radius * 0.8,
            (r[4] ?? 0) + (lift - 0.7) * this.radius * 1.1,
            z + oz * this.radius * 0.8,
          );
          this.dummy.scale.set(s, s, s);
        } else {
          const w = this.radius * 0.26 * sc; // wide, heavily overlapping puffs
          const h = this.radius * 0.11 * sc; // low — a bank on the water
          this.dummy.position.set(
            x + ox * this.radius * 0.85,
            this.waterY + 5 + this.radius * 0.05 * lift,
            z + oz * this.radius * 0.85,
          );
          this.dummy.scale.set(w, h, w);
        }
        this.dummy.rotation.y = yaw;
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(n++, this.dummy.matrix);
      }
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}

/**
 * Shell contrails: thin white streak segments laid down the flight path of
 * every main-battery round, tiling into one long unbroken vapor line (the
 * WoWS look) instead of a chain of smoke puffs. Each segment is a slim
 * instanced box trailing the shell backwards along its velocity; with age it
 * thins toward a pale haze and fades toward the sky tint, so an old trail
 * hangs as a faint chalk line rather than blocky smoke.
 */
export class ShellTrails {
  constructor(scene, capacity = 900) {
    this.capacity = capacity;
    this.mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.3, depthWrite: false }),
      capacity,
    );
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(capacity * 3),
      3,
    );
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.segs = [];
    this.dummy = new THREE.Object3D();
    this.cWhite = new THREE.Color(0xffffff);
    this.cFade = new THREE.Color(0xcfdbe6); // pale sky-haze the trail dies into
    this.cTmp = new THREE.Color();
  }

  /** Lay one segment ending at the shell's position, trailing back spanSec of
   * its velocity — called on a cadence so consecutive calls tile a line. */
  add(x, y, z, vx, vy, vz, spanSec) {
    if (this.segs.length >= this.capacity) this.segs.shift(); // drop the oldest
    this.segs.push({
      x, y, z,
      dx: -vx * spanSec, dy: -vy * spanSec, dz: -vz * spanSec,
      life: 2.6, maxLife: 2.6,
    });
  }

  update(dt) {
    let n = 0;
    const keep = [];
    for (const s of this.segs) {
      s.life -= dt;
      if (s.life <= 0) continue;
      keep.push(s);
      const t = s.life / s.maxLife; // 1 fresh -> 0 dead
      this.dummy.position.set(s.x + s.dx / 2, s.y + s.dy / 2, s.z + s.dz / 2);
      this.dummy.lookAt(s.x, s.y, s.z);
      // A thin bright core that disperses into a slightly wider, fainter haze.
      const w = 0.26 + (1 - t) * 0.5;
      const len = Math.hypot(s.dx, s.dy, s.dz) * 1.15; // slight overlap: no dashes
      this.dummy.scale.set(w, w, Math.max(len, 0.5));
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(n, this.dummy.matrix);
      this.mesh.setColorAt(n, this.cTmp.copy(this.cFade).lerp(this.cWhite, t));
      n++;
    }
    this.segs = keep;
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}

// High-altitude contrails (roadmap #45): the ShellTrails recipe with a much
// longer life, wind drift, and a width ramp so the band edge doesn't flicker.
// One InstancedMesh, one draw call; oldest segment drops when the pool fills.
export class PlaneContrails {
  constructor(scene, capacity = 1500) {
    this.capacity = capacity;
    this.mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.42, depthWrite: false }),
      capacity,
    );
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.segs = [];
    this.dummy = new THREE.Object3D();
    this.cWhite = new THREE.Color(0xffffff);
    this.cFade = new THREE.Color(0xd8e2ec);
    this.cTmp = new THREE.Color();
  }

  /** Emit cadence, stretched when the pool runs hot so long fights degrade
   * to sparser (not shorter) trails. */
  cadence() {
    return this.segs.length > this.capacity * 0.8 ? 0.35 : 0.18;
  }

  /** Lay one segment ending at the nacelle, trailing back spanSec of flight.
   * k (0..1) ramps the width in from the band floor. */
  add(x, y, z, vx, vy, vz, spanSec, k = 1, lifeSec = 22) {
    if (this.segs.length >= this.capacity) this.segs.shift();
    this.segs.push({
      x, y, z,
      dx: -vx * spanSec, dy: -vy * spanSec, dz: -vz * spanSec,
      life: lifeSec, maxLife: lifeSec, k,
    });
  }

  update(dt, windX = 0, windZ = 0) {
    let n = 0;
    const keep = [];
    const wx = windX * dt, wz = windZ * dt;
    for (const s of this.segs) {
      s.life -= dt;
      if (s.life <= 0) continue;
      s.x += wx;
      s.z += wz;
      keep.push(s);
      const t = s.life / s.maxLife;
      this.dummy.position.set(s.x + s.dx / 2, s.y + s.dy / 2, s.z + s.dz / 2);
      this.dummy.lookAt(s.x, s.y, s.z);
      // Fresh core spreading into a wide soft rope as it ages.
      const w = (0.5 + (1 - t) * 2.2) * s.k;
      const len = Math.hypot(s.dx, s.dy, s.dz) * 1.15;
      this.dummy.scale.set(w, w, Math.max(len, 0.5));
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(n, this.dummy.matrix);
      this.mesh.setColorAt(n, this.cTmp.copy(this.cFade).lerp(this.cWhite, t * 0.6 + 0.4));
      n++;
    }
    this.segs = keep;
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}

// G-pull wingtip streamers (roadmap #45): brief thin white cones snapping off
// the tips in a hard turn — width rides how deep past the G threshold.
export class Streamers {
  constructor(scene, capacity = 200) {
    this.pool = new PlaneContrails(scene, capacity);
    this.pool.mesh.material.opacity = 0.5;
  }

  add(x, y, z, vx, vy, vz, spanSec, w) {
    this.pool.add(x, y, z, vx, vy, vz, spanSec, 0.16 + w * 0.3, 0.7);
  }

  update(dt) {
    this.pool.update(dt);
  }
}

export class Particles {
  constructor(scene, capacity = 700) {
    this.capacity = capacity;
    this.mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({ color: 0xffffff }),
      capacity,
    );
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(capacity * 3),
      3,
    );
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);

    // Self-lit glow layer for fire: unlit + additive so muzzle flash blooms
    // bright at night (overlapping particles sum to a white-hot core), unlike
    // the scene-lit cubes above which go dark without daylight.
    this.glowCap = 256;
    this.glowMesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        fog: false,
      }),
      this.glowCap,
    );
    this.glowMesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(this.glowCap * 3),
      3,
    );
    this.glowMesh.frustumCulled = false;
    scene.add(this.glowMesh);

    this.dummy = new THREE.Object3D();
    this.parts = [];
    this.color = new THREE.Color();
  }

  spawn(pos, { count, speed, life, size, colors, gravity = 9.81, drag = 0.6, dir = null, spread = 1, spin = null, grow = false, glow = false }) {
    for (let i = 0; i < count; i++) {
      if (this.parts.length >= this.capacity) this.parts.shift();
      // Random sphere, optionally biased toward `dir` (spread 1 = fully
      // random, 0 = a tight jet along dir) for directional blasts.
      const d = new THREE.Vector3(
        Math.random() * 2 - 1,
        Math.random() * 2 - 1,
        Math.random() * 2 - 1,
      ).normalize();
      if (dir) d.lerp(dir, 1 - spread).normalize();
      this.parts.push({
        pos: pos.clone(),
        vel: d.multiplyScalar(speed * (0.3 + Math.random() * 0.7)),
        life: life * (0.6 + Math.random() * 0.4),
        maxLife: life,
        size: size * (0.6 + Math.random() * 0.8),
        color: colors[(Math.random() * colors.length) | 0],
        gravity,
        drag,
        // Low, steady tumble for smoke; chaotic for debris (default).
        spin: spin != null ? spin * (0.5 + Math.random()) : Math.random() * 4,
        grow,
        glow,
      });
    }
  }

  explosion(pos) {
    this.spawn(pos, {
      count: 38,
      speed: 42,
      life: 1.4,
      size: 2.6,
      colors: [0xff7b2e, 0xffc14d, 0x3a3a3a, 0x222222, 0xff4d2e],
    });
  }

  // Bomb detonation: a bigger fireball plus a column of thrown-up debris.
  bombBlast(pos) {
    this.spawn(pos, {
      count: 50,
      speed: 55,
      life: 1.7,
      size: 3.2,
      colors: [0xff7b2e, 0xffc14d, 0x3a3a3a, 0x222222, 0xff4d2e],
    });
    this.spawn(pos, {
      count: 20,
      speed: 38,
      life: 2.2,
      size: 2.4,
      colors: [0x5a4a32, 0x6b5b40, 0x44382a],
      gravity: 22, // dirt goes up fast and rains back down
      drag: 0.25,
    });
  }

  // Main-battery shell impact: a taller fireball and a heavier debris column
  // than a bomb — naval rifles announce themselves.
  shellBlast(pos) {
    this.spawn(pos, {
      count: 70,
      speed: 70,
      life: 2.0,
      size: 3.8,
      colors: [0xff7b2e, 0xffc14d, 0x3a3a3a, 0x222222, 0xff4d2e],
    });
    this.spawn(pos, {
      count: 30,
      speed: 48,
      life: 2.6,
      size: 2.8,
      colors: [0x5a4a32, 0x6b5b40, 0x44382a],
      gravity: 24,
      drag: 0.22,
    });
  }

  // Naval rifle firing: a hard white-gold muzzle flash thrown out along the
  // bore, wrapped in a rolling cloud of brown-grey gunsmoke that hangs and
  // rises off the barrels. k scales the whole blast by gun weight (1 = the
  // full battleship broadside; a destroyer's 5in passes ~0.45).
  muzzleBlast(pos, dir, k = 1) {
    const n = (c) => Math.max(3, Math.round(c * k)); // particle counts
    const s = 0.55 + 0.45 * k; // sizes: even a small gun keeps some presence
    // Smoke lifts up off the bore (buoyancy + a little downrange push), not
    // straight downrange.
    const smokeDir = new THREE.Vector3(0, 1, 0).addScaledVector(dir, 0.5).normalize();
    // Muzzle flash, after an Iowa-class broadside: a huge fireball that blooms
    // forward off the bore — white-hot at the core, golden-orange through the
    // body, amber at the burning edges — far bigger than the barrels. The
    // fireball sits ahead of the muzzle (as on a real broadside), which also
    // keeps it clear of the smoke rising at the bore so it reads in any light.
    const tip = pos.clone().addScaledVector(dir, 3.5 * s);
    // Fireball body: a big forward-blooming ball of yellow-orange fire. Lit
    // (opaque) so it has presence in daylight.
    this.spawn(tip, {
      count: n(44), speed: 38 * s, life: 0.85, size: 8.2 * s,
      colors: [0xffc14d, 0xffb347, 0xff9a3d, 0xffd76b, 0xff8a2e],
      gravity: 0, drag: 0.74, dir, spread: 0.5, grow: true,
    });
    // Fireball bloom: an additive glow over the body so it flares at dusk and
    // night, where the lit cubes would go dark. Kept golden (not white) so the
    // additive overlap builds toward amber, with white reserved for the core.
    this.spawn(tip, {
      count: n(34), speed: 30 * s, life: 0.75, size: 9.0 * s,
      colors: [0xffb347, 0xff9a3d, 0xffc14d, 0xffa838],
      gravity: 0, drag: 0.82, dir, spread: 0.55, grow: true, glow: true,
    });
    // White-hot core: a small, brief, intense flash at the muzzle.
    this.spawn(pos, {
      count: n(8), speed: 12 * s, life: 0.3, size: 6.5 * s,
      colors: [0xffffff, 0xfff0c0, 0xffe9a8],
      gravity: 0, drag: 1.0, dir, spread: 0.6, grow: true, glow: true,
    });
    // Burning amber edges and a lance of sparks thrown downrange.
    this.spawn(tip, {
      count: n(26), speed: 100 * s, life: 0.78, size: 4.4 * s,
      colors: [0xff8a2e, 0xffae4d, 0xff6a1e, 0xffd76b],
      gravity: 0, drag: 0.5, dir, spread: 0.26, grow: true, glow: true,
    });
    // Gunsmoke: a dense, slow, buoyant plume that billows up and disperses.
    // Light greys so it reads as a smoke column against dusk and night skies.
    this.spawn(pos, {
      count: n(44), speed: 8 * s, life: 3.2, size: 4.2 * s,
      colors: [0xc4c4c4, 0xd2ccc0, 0xa8a298, 0xe0d9cc, 0x8f897e],
      gravity: -7, drag: 0.92, dir: smokeDir, spread: 0.45, spin: 0.5, grow: true,
    });
    // A low cordite ring hugging the muzzle as the column lifts.
    this.spawn(pos, {
      count: n(16), speed: 22 * s, life: 1.1, size: 3.0 * s,
      colors: [0xcfc9bd, 0xe2dbcd, 0xada79b],
      gravity: -3, drag: 0.9, dir, spread: 0.6, spin: 0.6, grow: true,
    });
  }

  // A single drifting vapor puff, dropped repeatedly along a shell's path to
  // leave a contrail hanging in the air.
  vapor(pos) {
    this.spawn(pos, {
      count: 1, speed: 1.8, life: 1.7, size: 2.3,
      colors: [0xeef2f6, 0xd8dde2, 0xc4cace, 0xb6bcc2],
      gravity: -0.8, drag: 1.3, spin: 0.25, grow: true,
    });
  }

  // Shell short into the sea: a tall white water column.
  shellSplash(pos, { big = false } = {}) {
    if (big) {
      // Naval fall-of-shot: a tall water column legible at the 3-8 km where you
      // walk main-battery fire onto a target (G3). Reading the short/over of the
      // splash is the aim-correction loop, so it has to be visible from range.
      this.spawn(pos, {
        count: 90,
        speed: 70,
        life: 2.6,
        size: 4.6,
        colors: [0x9fd4f5, 0xd5ecfa, 0x4a90c9, 0xeef7fc],
        gravity: 16,
        drag: 0.28,
      });
      // A slim white core spike so it reads as a rising column, not a puff.
      this.spawn(pos, {
        count: 18,
        speed: 85,
        life: 2.2,
        size: 3,
        colors: [0xffffff, 0xeef7fc],
        gravity: 20,
        drag: 0.2,
        spread: 0.12,
        dir: new THREE.Vector3(0, 1, 0),
      });
      return;
    }
    this.spawn(pos, {
      count: 46,
      speed: 44,
      life: 1.6,
      size: 2.6,
      colors: [0x9fd4f5, 0xd5ecfa, 0x4a90c9, 0xeef7fc],
      gravity: 16,
      drag: 0.3,
    });
  }

  hitSpark(pos) {
    // A round cracking off steel: a hot WHITE flash with a spray of tiny
    // sparks. Small, but genuinely bright — incandescent, not "yellow".
    //
    // The brightness trick is overlap. One additive cube at 0.5 opacity barely
    // shows over daylit terrain; a tight, slow-moving KNOT of them lands on the
    // same pixels and stacks past white-hot. So the core is clustered and slow
    // (they sit on top of each other), the spray is fast (it streaks out), and
    // both are additive so they glow at any hour — brightest at dusk, where
    // scene-lit debris would just go dark.
    //
    // White-dominant on purpose: a spark reads as heat, not paint. The warm
    // tints only tail the white so the edges go amber as it cools.

    // Solid white-hot core: opaque, scene-lit, PURE white — the one thing that
    // reads as bright white even in full daylight. Additive glow alone only
    // *adds* to an already-bright scene and washes out; a lit white cube is the
    // brightest thing on screen. Tight and slow (high drag) so it's a hot
    // pinpoint, not a cloud.
    this.spawn(pos, {
      count: 5,
      speed: 6,
      life: 0.12,
      size: 0.32,
      colors: [0xffffff],
      gravity: 0,
      drag: 2.2,
    });
    // Additive bloom over that core: makes it flare white-hot at dusk and night,
    // where the lit cube above goes dark. Overlapping instances stack past white.
    this.spawn(pos, {
      count: 7,
      speed: 4,
      life: 0.14,
      size: 0.34,
      colors: [0xffffff, 0xfff6e6],
      gravity: 0,
      drag: 2.4,
      glow: true,
    });
    // Spark spray: small, fast, white-hot pinpoints streaking outward and
    // winking as they cool to amber.
    this.spawn(pos, {
      count: 10,
      speed: 54,
      life: 0.26,
      size: 0.15,
      colors: [0xffffff, 0xffffff, 0xfff4d6, 0xffcf7a],
      gravity: 8,
      drag: 0.32,
      glow: true,
    });
    // A couple of amber embers with a little hang, scene-lit, for body — so the
    // burst isn't a pure flash and a fragment or two arcs off the plate.
    this.spawn(pos, {
      count: 2,
      speed: 22,
      life: 0.36,
      size: 0.22,
      colors: [0xffc978, 0xffaa55],
      gravity: 10,
      drag: 0.5,
    });
  }

  splash(pos) {
    this.spawn(pos, {
      count: 22,
      speed: 30,
      life: 1.0,
      size: 1.8,
      colors: [0x9fd4f5, 0xd5ecfa, 0x4a90c9],
    });
  }

  fire(pos) {
    this.spawn(pos, {
      count: 2,
      speed: 5,
      life: 0.55,
      size: 1.3,
      colors: [0xff7b2e, 0xffae3d, 0xff4d2e, 0x2e2e2e],
      gravity: -8, // flames lick upward
      drag: 1.2,
    });
  }

  smoke(pos) {
    this.spawn(pos, {
      count: 1,
      speed: 3,
      life: 1.6,
      size: 1.7,
      colors: [0x4d4d4d, 0x6b6b6b, 0x303030],
      gravity: -3, // smoke rises
      drag: 1.4,
    });
  }

  update(dt) {
    let i = 0;
    for (let k = 0; k < this.parts.length; k++) {
      const p = this.parts[k];
      p.life -= dt;
      if (p.life <= 0) continue;
      this.parts[i++] = p;
      p.vel.y -= p.gravity * dt;
      p.vel.multiplyScalar(Math.max(0, 1 - p.drag * dt));
      p.pos.addScaledVector(p.vel, dt);
    }
    this.parts.length = i;

    let litN = 0;
    let glowN = 0;
    for (let k = 0; k < i; k++) {
      const p = this.parts[k];
      const t = p.life / p.maxLife;
      this.dummy.position.copy(p.pos);
      this.dummy.rotation.set(p.spin * p.life, p.spin, 0);
      // Debris shrinks as it dies; smoke/vapor/fire (grow) billows outward as
      // it ages and then thins away over its last quarter, so a dense spawn
      // reads as a rising, dispersing cloud rather than a pop of blocks.
      let s;
      if (p.grow) {
        const age = 1 - t;
        const tail = Math.min(1, t / 0.28);
        s = p.size * (0.5 + age * 0.95) * tail;
      } else {
        s = p.size * (0.4 + 0.6 * t);
      }
      this.dummy.scale.set(s, s, s);
      this.dummy.updateMatrix();
      if (p.glow) {
        if (glowN >= this.glowCap) continue;
        this.glowMesh.setMatrixAt(glowN, this.dummy.matrix);
        this.glowMesh.setColorAt(glowN, this.color.setHex(p.color));
        glowN++;
      } else {
        if (litN >= this.capacity) continue;
        this.mesh.setMatrixAt(litN, this.dummy.matrix);
        this.mesh.setColorAt(litN, this.color.setHex(p.color));
        litN++;
      }
    }
    this.mesh.count = litN;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.glowMesh.count = glowN;
    this.glowMesh.instanceMatrix.needsUpdate = true;
    if (this.glowMesh.instanceColor) this.glowMesh.instanceColor.needsUpdate = true;
  }
}
